import React from "react";
import { useRealTimeCharacterCounter } from "../../hooks/use-real-time-character-counter";
import type { CharacterCountInfo } from "../../types/start-message-types";

interface RealTimeCharacterCounterProps {
  content: string;
  onContentChange: (content: string) => void;
  className?: string;
  showWarnings?: boolean;
  enableRealTimeUpdates?: boolean;
  updateInterval?: number;
}

export function RealTimeCharacterCounter({
  content,
  onContentChange,
  className = "",
  showWarnings = true,
  enableRealTimeUpdates = true,
  updateInterval = 100,
}: RealTimeCharacterCounterProps): React.ReactElement {
  const { countInfo, isTyping, lastUpdated } = useRealTimeCharacterCounter({
    initialContent: content,
    updateInterval,
    enableRealTimeUpdates,
  });

  // Get color classes based on status
  const getColorClasses = () => {
    switch (countInfo.status) {
      case "error":
        return "text-red-600 bg-red-50 border-red-200";
      case "warning":
        return "text-orange-600 bg-orange-50 border-orange-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  // Get progress bar color
  const getProgressColor = () => {
    switch (countInfo.status) {
      case "error":
        return "bg-red-500";
      case "warning":
        return "bg-orange-500";
      default:
        return "bg-blue-500";
    }
  };

  // Get typing indicator color
  const getTypingIndicatorColor = () => {
    if (!isTyping) return "bg-gray-300";

    switch (countInfo.status) {
      case "error":
        return "bg-red-400";
      case "warning":
        return "bg-orange-400";
      default:
        return "bg-blue-400";
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Character count display with real-time indicator */}
      <div className="flex items-center justify-between">
        <div
          className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getColorClasses()}`}
        >
          <span className="mr-1">{countInfo.count}/140</span>
          {countInfo.remaining >= 0 ? (
            <span className="text-xs opacity-75">
              ({countInfo.remaining} remaining)
            </span>
          ) : (
            <span className="text-xs opacity-75">
              ({Math.abs(countInfo.remaining)} over limit)
            </span>
          )}
        </div>

        {/* Real-time typing indicator */}
        <div className="flex items-center space-x-2">
          {isTyping && (
            <div className="flex items-center text-xs text-gray-500">
              <div
                className={`w-2 h-2 rounded-full mr-1 animate-pulse ${getTypingIndicatorColor()}`}
              />
              <span>Updating...</span>
            </div>
          )}

          {lastUpdated && !isTyping && (
            <div className="text-xs text-gray-400">
              Updated {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* Progress bar with animation */}
      <div className="w-full bg-gray-200 rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full transition-all duration-300 ${getProgressColor()}`}
          style={{
            width: `${Math.min(countInfo.percentage, 100)}%`,
          }}
        />
      </div>

      {/* Warning messages */}
      {showWarnings && (
        <>
          {countInfo.status === "warning" && (
            <div className="flex items-center text-xs text-orange-600 animate-pulse">
              <svg
                className="w-3 h-3 mr-1 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <span>Approaching character limit</span>
            </div>
          )}
          {countInfo.status === "error" && (
            <div className="flex items-center text-xs text-red-600 animate-pulse">
              <svg
                className="w-3 h-3 mr-1 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Message exceeds character limit</span>
            </div>
          )}
        </>
      )}

      {/* Info text with real-time status */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>20 characters reserved for tracking data</span>

        {enableRealTimeUpdates && (
          <div className="flex items-center">
            <div
              className={`w-1.5 h-1.5 rounded-full mr-1 ${
                isTyping ? "bg-green-400 animate-pulse" : "bg-gray-300"
              }`}
            />
            <span>Real-time</span>
          </div>
        )}
      </div>
    </div>
  );
}
